import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Calendar, MapPin, Clock, Users, Filter, Repeat } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getActivities, createActivityRegistration } from "@/lib/api";
import { formatDateLong, formatTime, formatMonth } from "@/lib/dateUtils";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import RecaptchaWrapper from "@/components/RecaptchaWrapper";

const HERO_IMAGE = "https://images.pexels.com/photos/8224722/pexels-photo-8224722.jpeg";

const categoryLabels = {
  sport: { label: "Sport", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  workshop: { label: "Workshop", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  evenement: { label: "Evenement", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  training: { label: "Training", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" }
};

const categoryFilters = [
  { value: "all", label: "Alle" },
  { value: "sport", label: "Sport" },
  { value: "workshop", label: "Workshop" },
  { value: "evenement", label: "Evenement" },
  { value: "training", label: "Training" }
];

const emptyRegistration = {
  name: "",
  email: "",
  phone: "",
  message: ""
};

function SportEldoradoKalenderPage() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registrationForm, setRegistrationForm] = useState(emptyRegistration);
  const [submitting, setSubmitting] = useState(false);
  const { verifyRecaptcha } = useRecaptcha();

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const params = selectedCategory !== "all" ? { category: selectedCategory } : {};
        const res = await getActivities(params);
        setActivities(res.data);
      } catch (error) {
        console.error("Error fetching activities:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, [selectedCategory]);

  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setDetailDialogOpen(true);
  };

  const handleOpenRegister = () => {
    setRegistrationForm(emptyRegistration);
    setDetailDialogOpen(false);
    setRegisterDialogOpen(true);
  };

  const handleSubmitRegistration = async () => {
    if (!registrationForm.name || !registrationForm.email) {
      toast.error("Naam en email zijn verplicht");
      return;
    }

    setSubmitting(true);
    try {
      // Verify reCAPTCHA first
      const recaptchaToken = await verifyRecaptcha("activity_registration");
      
      await createActivityRegistration({
        activity_id: selectedActivity.id,
        activity_title: selectedActivity.title,
        ...registrationForm,
        recaptcha_token: recaptchaToken
      });
      toast.success("Je bent ingeschreven! We nemen contact met je op.");
      setRegisterDialogOpen(false);
      setRegistrationForm(emptyRegistration);
    } catch (error) {
      console.error("Error registering:", error);
      toast.error("Kon inschrijving niet voltooien");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="sport-eldorado-kalender-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            <p className="text-[hsl(48,96%,53%)] font-medium mb-2">Sport Eldorado</p>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Chivo']">KALENDER</h1>
            <p className="text-slate-300 mt-2">Activiteiten en evenementen</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categoryFilters.map((cat) => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? "default" : "outline"}
              onClick={() => setSelectedCategory(cat.value)}
              className={selectedCategory === cat.value 
                ? "bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)]" 
                : isDark ? "border-white/20 text-slate-300 hover:bg-white/10" : "border-slate-300"
              }
            >
              <Filter className="h-4 w-4 mr-2" />
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Activities List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className={`animate-pulse ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
                <CardContent className="py-6">
                  <div className={`h-6 w-3/4 mb-4 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                  <div className={`h-4 w-full mb-2 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : activities.length > 0 ? (
          <div className="space-y-4">
            {activities.map((activity) => {
              const catInfo = categoryLabels[activity.category] || categoryLabels.sport;
              return (
                <Card 
                  key={activity.id}
                  className={`group cursor-pointer transition-all hover:scale-[1.01] ${isDark ? 'bg-slate-900/50 border-white/10 hover:border-[hsl(217,91%,60%)]/50' : 'bg-white border-slate-200 shadow-md hover:shadow-lg'}`}
                  onClick={() => handleActivityClick(activity)}
                >
                  <CardContent className="py-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Date Box */}
                      <div className={`flex-shrink-0 w-20 h-20 rounded-lg flex flex-col items-center justify-center ${isDark ? 'bg-[hsl(217,91%,60%)]/20' : 'bg-blue-100'}`}>
                        <span className={`text-2xl font-black ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`}>
                          {activity.date.split('-')[2] || activity.date.split('/')[0]}
                        </span>
                        <span className={`text-xs uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {formatMonth(activity.date)}
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={catInfo.color}>{catInfo.label}</Badge>
                          {activity.is_recurring && (
                            <Badge className={`${isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                              <Repeat className="h-3 w-3 mr-1" />
                              Wekelijks
                            </Badge>
                          )}
                        </div>
                        <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {activity.title}
                        </h3>
                        <div className="flex flex-wrap gap-4">
                          <div className={`flex items-center gap-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            <Clock className="h-4 w-4" />
                            <span>{activity.time}</span>
                          </div>
                          <div className={`flex items-center gap-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            <MapPin className="h-4 w-4" />
                            <span>{activity.location}</span>
                          </div>
                          {activity.max_participants && (
                            <div className={`flex items-center gap-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              <Users className="h-4 w-4" />
                              <span>Max {activity.max_participants} deelnemers</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className={`text-center py-16 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <CardContent>
              <Calendar className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Geen activiteiten gepland
              </h3>
              <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Er zijn momenteel geen activiteiten gepland in deze categorie.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Activity Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
          {selectedActivity && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={categoryLabels[selectedActivity.category]?.color || categoryLabels.sport.color}>
                    {categoryLabels[selectedActivity.category]?.label || "Sport"}
                  </Badge>
                </div>
                <DialogTitle className={`font-['Chivo'] text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {selectedActivity.title}
                </DialogTitle>
              </DialogHeader>
              
              {selectedActivity.image_url && (
                <img 
                  src={selectedActivity.image_url} 
                  alt={selectedActivity.title}
                  className="w-full h-48 object-cover rounded-lg"
                />
              )}
              
              <div className="space-y-4">
                <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  {selectedActivity.description}
                </p>
                
                <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                  <h4 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Details</h4>
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      <Calendar className="h-4 w-4" />
                      <span>{formatDateLong(selectedActivity.date)}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      <Clock className="h-4 w-4" />
                      <span>{selectedActivity.time}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      <MapPin className="h-4 w-4" />
                      <span>{selectedActivity.location}</span>
                    </div>
                    {selectedActivity.max_participants && (
                      <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        <Users className="h-4 w-4" />
                        <span>Maximum {selectedActivity.max_participants} deelnemers</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)]"
                  onClick={handleOpenRegister}
                >
                  Inschrijven
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Registration Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
          {selectedActivity && (
            <>
              <DialogHeader>
                <DialogTitle className={`font-['Chivo'] text-xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Inschrijven: {selectedActivity.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Naam *</Label>
                  <Input
                    value={registrationForm.name}
                    onChange={(e) => setRegistrationForm({...registrationForm, name: e.target.value})}
                    placeholder="Je volledige naam"
                    className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email *</Label>
                  <Input
                    type="email"
                    value={registrationForm.email}
                    onChange={(e) => setRegistrationForm({...registrationForm, email: e.target.value})}
                    placeholder="je@email.com"
                    className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Telefoon</Label>
                  <Input
                    value={registrationForm.phone}
                    onChange={(e) => setRegistrationForm({...registrationForm, phone: e.target.value})}
                    placeholder="+32 xxx xx xx xx"
                    className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Opmerkingen</Label>
                  <Textarea
                    value={registrationForm.message}
                    onChange={(e) => setRegistrationForm({...registrationForm, message: e.target.value})}
                    placeholder="Eventuele opmerkingen of vragen..."
                    rows={3}
                    className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                  />
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setRegisterDialogOpen(false)} disabled={submitting}>
                  Annuleren
                </Button>
                <Button 
                  onClick={handleSubmitRegistration} 
                  disabled={submitting}
                  className="bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)]"
                >
                  {submitting ? "Versturen..." : "Inschrijven"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SportEldoradoKalender() {
  return (
    <RecaptchaWrapper>
      <SportEldoradoKalenderPage />
    </RecaptchaWrapper>
  );
}
